<script lang="ts" setup>
import { computed, ref, nextTick, watch, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuasar } from 'quasar'
import { useRouter } from 'vue-router'
import { useIncidentSubmissionStore } from '../stores/incident-submission'
import { useFileUploadStore } from '../stores/file-upload'
import { useBountyResponseStore } from '../stores/bounty-response'
import { useUserStore } from '@/stores/user/user.store'
import AccountRestrictedBanner from '@/components/AccountRestrictedBanner.vue'

// Sub-components
import StepStart from '../components/incident-report/StepStart.vue'
import StepVendorBrowse from '../components/incident-report/StepVendorBrowse.vue'
import StepMediaUpload from '../components/incident-report/StepMediaUpload.vue'
import StepIncidentDetails from '../components/incident-report/StepIncidentDetails.vue'
import StepRefineLocation from '../components/incident-report/StepRefineLocation.vue'
import StepRecordedCrash from '../components/incident-report/StepRecordedCrash.vue'
import StepReviewSubmit from '../components/incident-report/StepReviewSubmit.vue'
import { shortReportRef } from '../utils/report-ref'
import { getExtractor } from '../utils/vendors/registry'

const $q = useQuasar()
const incidentStore = useIncidentSubmissionStore()
const fileUploadStore = useFileUploadStore()
const router = useRouter()
const bountyResponseStore = useBountyResponseStore()
const { isRestricted } = storeToRefs(useUserStore())
const { respondingTo } = storeToRefs(bountyResponseStore)
const { relatedTo } = storeToRefs(incidentStore)

// Leaving the wizard abandons the response, so a later unrelated report is never linked to the bounty.
onBeforeUnmount(() => {
  bountyResponseStore.cancelResponse()
  incidentStore.clearRelated()
})

// Step form refs
const mediaUploadFormRef = ref()
const incidentDetailsFormRef = ref()

const stepFormRefs: Record<string, any> = {
  'media-upload': mediaUploadFormRef,
  'incident-details': incidentDetailsFormRef
}

const isStepValid = ref(true)
const stepperRef = ref()

const { step, nearbyCrashes, isLoadingCrashes } = storeToRefs(incidentStore)

// Dynamically handle mobile vs desktop stepper layout
const isMobile = computed(() => $q.screen.lt.md)

// focus: move keyboard focus to the first invalid field (only when the user pressed Continue).
async function validateCurrentStep(focus = false): Promise<boolean> {
  const refObj = stepFormRefs[incidentStore.step]
  if (refObj && refObj.value && typeof refObj.value.validate === 'function') {
    const valid = await refObj.value.validate(focus)
    isStepValid.value = !!valid
    return !!valid
  }
  isStepValid.value = true
  return true
}

async function onContinue(): Promise<void> {
  const valid = await validateCurrentStep(true)
  if (!valid) return
  if (!incidentStore.isLastStep) {
    if (incidentStore.step === 'refine-location') await incidentStore.continueFromRefine()
    else incidentStore.nextStep()
    await nextTick()
    await validateCurrentStep()
  } else {
    submitReport()
  }
}

function skipRecordedCrash(): void {
  incidentStore.selectCrash(null)
  incidentStore.nextStep()
}

// Validate on step change, then move focus to the new step's heading so keyboard and screen reader users
// start at the top of the step instead of on a button that has just disappeared.
watch(
  () => incidentStore.step,
  async () => {
    await nextTick()
    await validateCurrentStep()
    focusStepHeading()
  }
)

function focusStepHeading() {
  const heading = stepperRef.value?.$el?.querySelector(
    `[data-step-name="${incidentStore.step}"] [data-step-heading]`
  ) as HTMLElement | null
  heading?.focus({ preventScroll: true })
}

async function submitReport(): Promise<void> {
  if (incidentStore.isSubmitting) return

  const reportId = await incidentStore.submitReport()

  if (!reportId) {
    $q.notify({
      type: 'negative',
      message: 'Failed to submit the report. Please try again.',
      position: 'top'
    })
    return // Exit early
  }

  $q.notify({
    type: 'positive',
    message: 'Incident report submitted successfully.',
    position: 'top'
  })

  // Create-then-link: the report exists now, so a failed link must not read as a failed submit.
  if (respondingTo.value) {
    const linked = await bountyResponseStore.submitResponse(reportId)
    bountyResponseStore.cancelResponse()
    if (!linked.ok) {
      $q.notify({
        type: 'warning',
        message: `Report submitted, but not linked to the bounty: ${linked.message} You can respond to the bounty with this report later.`,
        position: 'top',
        timeout: 8000
      })
    } else {
      $q.notify({
        type: 'positive',
        message: 'Response sent to the bounty poster.',
        position: 'top'
      })
    }
  }

  // Clear the stores so the form is fresh for next time
  incidentStore.$reset()
  fileUploadStore.$reset()

  // Route the user to the featured view
  router.push({ name: 'uploads' })
}
</script>

<template>
  <q-page padding class="bg-grey-2">
    <AccountRestrictedBanner v-if="isRestricted" />
    <div v-else class="row layout-wide q-mx-auto">
      <div class="col-12">
        <h4 class="text-h4 text-weight-bold q-mt-none q-mb-sm text-blue-grey-9">Share Footage</h4>
        <p class="text-body1 text-blue-grey-8">
          Your report will be reviewed by our team before it is shared.
        </p>

        <q-banner
          v-if="respondingTo"
          rounded
          class="bg-blue-1 text-blue-grey-9 q-mt-md"
          data-testid="bounty-banner"
        >
          Responding to the bounty at
          <span class="text-weight-bold">{{
            respondingTo.address?.formattedAddress || 'an unknown location'
          }}</span
          >. Your report will be sent to the poster once it is submitted.
          <template #action>
            <q-btn
              flat
              no-caps
              color="blue-8"
              label="Cancel"
              :disable="incidentStore.isSubmitting"
              @click="bountyResponseStore.cancelResponse()"
            />
          </template>
        </q-banner>

        <q-banner
          v-if="relatedTo"
          rounded
          class="bg-blue-1 text-blue-grey-9 q-mt-md"
          data-testid="related-banner"
        >
          Sharing footage of the same incident as
          <span class="text-weight-bold">{{ shortReportRef(relatedTo.id) }}</span
          >. Staff will check the link before it is shown.
          <template #action>
            <q-btn
              flat
              no-caps
              color="blue-8"
              label="Cancel"
              :disable="incidentStore.isSubmitting"
              @click="incidentStore.clearRelated()"
            />
          </template>
        </q-banner>

        <q-slide-transition>
          <q-banner
            v-if="fileUploadStore.isPreparing || fileUploadStore.isUploading"
            rounded
            class="bg-blue-grey-9 text-white q-mt-md"
          >
            <div class="row items-center no-wrap">
              <q-spinner-dots
                v-if="fileUploadStore.isPreparing"
                size="2em"
                color="blue-8"
                class="q-mr-sm"
              />
              <div class="col">
                <span class="text-weight-bold">
                  {{ fileUploadStore.isPreparing ? 'Preparing files...' : 'Uploading media...' }}
                </span>
                <span v-if="fileUploadStore.isUploading" class="text-caption q-ml-sm">
                  ({{ fileUploadStore.uploadedFiles.length }} /
                  {{ fileUploadStore.userFileList.length }})
                </span>
                <q-linear-progress
                  v-if="fileUploadStore.isUploading"
                  aria-label="Upload progress"
                  :value="fileUploadStore.uploadProgress"
                  color="blue-8"
                  track-color="blue-grey-7"
                  class="q-mt-sm"
                  rounded
                />
              </div>
            </div>
          </q-banner>
        </q-slide-transition>
      </div>

      <div class="col-12 q-mt-md">
        <q-stepper
          ref="stepperRef"
          v-model="step"
          color="blue-8"
          inactive-color="grey-7"
          animated
          flat
          bordered
          :vertical="isMobile"
          :alternative-labels="!isMobile"
          class="bg-white"
        >
          <q-step
            name="start"
            data-step-name="start"
            title="Start"
            icon="mdi-camera"
            :done="incidentStore.isStepDone('start')"
          >
            <step-start />
            <q-stepper-navigation class="row items-center justify-start q-gutter-x-sm">
              <q-btn
                @click="onContinue"
                color="blue-8"
                unelevated
                label="Continue"
                class="q-px-lg text-weight-bold"
                :disable="incidentStore.isSubmitting"
              />
            </q-stepper-navigation>
          </q-step>

          <q-step
            v-if="getExtractor(incidentStore.incidentForm.deviceVendor).supportsDirectoryBrowse"
            name="vendor-browse"
            data-step-name="vendor-browse"
            title="Browse"
            icon="mdi-folder-open"
            :done="incidentStore.isStepDone('vendor-browse')"
          >
            <step-vendor-browse />
            <q-stepper-navigation class="row items-center justify-start q-gutter-x-sm">
              <q-btn
                flat
                color="blue-8"
                @click="incidentStore.nextStep()"
                label="Skip — pick files manually"
                class="q-px-md text-weight-bold"
                :disable="incidentStore.isSubmitting"
              />
              <q-btn
                flat
                color="blue-8"
                @click="incidentStore.previousStep()"
                label="Back"
                class="q-px-md text-weight-bold"
                :disable="incidentStore.isSubmitting"
              />
            </q-stepper-navigation>
          </q-step>

          <q-step
            name="media-upload"
            data-step-name="media-upload"
            title="Upload"
            icon="mdi-cloud-upload"
            :done="incidentStore.isStepDone('media-upload')"
          >
            <step-media-upload ref="mediaUploadFormRef" />
            <q-stepper-navigation class="row items-center justify-start q-gutter-x-sm">
              <q-btn
                @click="onContinue"
                color="blue-8"
                unelevated
                label="Continue"
                class="q-px-lg text-weight-bold"
                :disable="incidentStore.isSubmitting || !fileUploadStore.hasFiles"
              />
              <q-btn
                flat
                color="blue-8"
                @click="incidentStore.previousStep()"
                label="Back"
                class="q-px-md text-weight-bold"
                :disable="incidentStore.isSubmitting"
              />
            </q-stepper-navigation>
          </q-step>

          <q-step
            name="incident-details"
            data-step-name="incident-details"
            title="Details"
            icon="mdi-card-text"
            :done="incidentStore.isStepDone('incident-details')"
          >
            <step-incident-details ref="incidentDetailsFormRef" />
            <q-stepper-navigation class="row items-center justify-start q-gutter-x-sm">
              <q-btn
                @click="onContinue"
                color="blue-8"
                unelevated
                label="Continue"
                class="q-px-lg text-weight-bold"
                :disable="incidentStore.isSubmitting"
              />
              <q-btn
                flat
                color="blue-8"
                @click="incidentStore.previousStep()"
                label="Back"
                class="q-px-md text-weight-bold"
                :disable="incidentStore.isSubmitting"
              />
            </q-stepper-navigation>
          </q-step>

          <q-step
            name="refine-location"
            data-step-name="refine-location"
            title="Refine"
            icon="mdi-map-marker"
            :done="incidentStore.isStepDone('refine-location')"
          >
            <step-refine-location />
            <q-stepper-navigation class="row items-center justify-start q-gutter-x-sm">
              <q-btn
                @click="onContinue"
                color="blue-8"
                unelevated
                label="Continue"
                class="q-px-lg text-weight-bold"
                :loading="isLoadingCrashes"
                :disable="incidentStore.isSubmitting"
              />
              <q-btn
                flat
                color="blue-8"
                @click="incidentStore.previousStep()"
                label="Back"
                class="q-px-md text-weight-bold"
                :disable="incidentStore.isSubmitting || isLoadingCrashes"
              />
            </q-stepper-navigation>
          </q-step>

          <q-step
            v-if="nearbyCrashes.length > 0"
            name="recorded-crash"
            data-step-name="recorded-crash"
            title="Crash?"
            icon="mdi-alert-circle-outline"
            :done="incidentStore.isStepDone('recorded-crash')"
          >
            <step-recorded-crash />
            <q-stepper-navigation class="row items-center justify-start q-gutter-x-sm">
              <q-btn
                @click="onContinue"
                color="blue-8"
                unelevated
                label="Continue"
                class="q-px-lg text-weight-bold"
                :disable="incidentStore.isSubmitting"
              />
              <q-btn
                flat
                color="blue-8"
                @click="skipRecordedCrash"
                label="None of these"
                class="q-px-md text-weight-bold"
                :disable="incidentStore.isSubmitting"
                data-testid="recorded-crash-skip"
              />
              <q-btn
                flat
                color="blue-8"
                @click="incidentStore.previousStep()"
                label="Back"
                class="q-px-md text-weight-bold"
                :disable="incidentStore.isSubmitting"
              />
            </q-stepper-navigation>
          </q-step>

          <q-step
            name="review-submit"
            data-step-name="review-submit"
            title="Review"
            icon="mdi-check-all"
            :done="incidentStore.isStepDone('review-submit')"
          >
            <step-review-submit />
            <q-stepper-navigation class="row items-center justify-start q-gutter-x-sm">
              <q-btn
                @click="onContinue"
                color="blue-8"
                unelevated
                label="Submit Report"
                class="q-px-lg text-weight-bold"
                :disable="incidentStore.isSubmitting"
              />
              <q-btn
                flat
                color="blue-8"
                @click="incidentStore.previousStep()"
                label="Back"
                class="q-px-md text-weight-bold"
                :disable="incidentStore.isSubmitting"
              />
            </q-stepper-navigation>
          </q-step>
        </q-stepper>
      </div>
    </div>
  </q-page>
</template>
